import React, { useState, useEffect } from 'react';

import { DeleteOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import FloatingActionBar, { FloatingActionBarSpacer } from '@components/shared/ui/FloatingActionBar';
import ProductSelect from '@components/shared/products/ProductSelect';
import useSmartBack from '@hooks/shared/useSmartBack';
import { handleApiError, handleNetworkError } from '@utils/apiErrorHandler';
import { Form, Input, Button, Card, message, Select, Table, Alert, Space, Spin, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

interface FlexibleProductLoadedPayload {
  merchant_id?: string | null;
  merchant?: { id?: string | null; name?: string | null } | null;
  [key: string]: unknown;
}

interface SelectedProductOption {
  value: string;
  label: string;
  sku?: string;
  name?: string;
  [key: string]: unknown;
}

interface FlexibleProductEditViewProps {
  // flexibleProductService 是 .js service 物件，admin/operator 兩套各有不同 method 簽名
  // (get/create/update/...)，沿用 InboundEdit 的 `any` 邊界放行。
  flexibleProductService: any;
  productService?: unknown;
  role?: 'admin' | 'operator';
  // The merchant this form is operating on.
  //   - NEW (operator): the merchant chosen via <MerchantSelect> / OperatorDataContext.
  //   - NEW (admin): admin session is already merchant-locked; this can be omitted.
  //   - EDIT: the record's merchant_id (wrapper feeds it via useRecordMerchantSync ⇒
  //     currentMerchant has been synced to the record before this prop is read).
  // Used both as the create-payload `merchant_id` (NEW only) and as the
  // ProductSelect / reference-data filter (NEW & EDIT).
  merchantId?: string | null;
  // 編輯模式：shared 拿到 API response 後通知 wrapper 把 currentMerchant 對齊 record。
  onFlexibleProductLoaded?: (record: FlexibleProductLoadedPayload) => void;
  // True while wrapper is still syncing context → record's merchant.
  // 與內部 dataLoading 一起 gate render，避免 ProductSelect 暫時用錯 merchantId。
  merchantDataLoading?: boolean;
}

/**
 * FlexibleProductEditView - 通用彈性置換商品編輯組件
 *
 * 注意：PageHeader 由外層 wrapper 負責渲染（admin/operator 各自的 Edit.jsx / New.jsx），
 * 本組件不再自行渲染 PageHeader 以避免 breadcrumb 重複。
 *
 * Merchant scope：本組件不讀 OperatorDataContext，merchantId 永遠由 wrapper 傳入：
 * - 新增模式：由 MerchantSelect 提供
 * - 編輯模式：由 useRecordMerchantSync 提供（record-first）
 */
const FlexibleProductEditView: React.FC<FlexibleProductEditViewProps> = ({
  flexibleProductService,
  productService,
  role = 'admin',
  merchantId = null,
  onFlexibleProductLoaded,
  merchantDataLoading = false,
}) => {
  const { id } = useParams<{ id?: string }>();
  const goBack = useSmartBack();
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState<boolean>(Boolean(id));

  const [selectedProducts, setSelectedProducts] = useState<SelectedProductOption[]>([]);

  useEffect(() => {
    if (id) {
      loadFlexibleProduct();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadFlexibleProduct = async () => {
    try {
      setDataLoading(true);
      const response = await flexibleProductService.get(id);
      if (response.success) {
        const flexibleProduct = response.data as FlexibleProductLoadedPayload & {
          name?: string;
          sku?: string;
          mechanism?: string;
          products?: Array<{ id: string; sku: string; name: string }>;
        };
        // 通知 wrapper：拿到 record 後讓它把 OperatorDataContext.currentMerchant
        // 對齊到該 record 的 merchant（由 useRecordMerchantSync 處理）。shared
        // 本身不再讀 / 寫 merchant context，保持 merchant-agnostic。
        onFlexibleProductLoaded?.(flexibleProduct);

        form.setFieldsValue({
          name: flexibleProduct.name,
          sku: flexibleProduct.sku,
          mechanism: flexibleProduct.mechanism,
        });

        if (flexibleProduct.products && flexibleProduct.products.length > 0) {
          setSelectedProducts(flexibleProduct.products.map(p => ({
            value: p.id,
            label: `${p.sku} - ${p.name}`,
            sku: p.sku,
            name: p.name,
          })));
        }
      } else {
        handleApiError(response, { t, fallbackMessage: t('common.error.load_failed') });
      }
    } catch (error) {
      console.error('Load flexible product error:', error);
      handleNetworkError(error, { t });
    } finally {
      setDataLoading(false);
    }
  };

  const handleSelectProduct = (productId: string | null | undefined, option: SelectedProductOption | undefined) => {
    if (!productId || !option) return;
    setSelectedProducts([...selectedProducts, option]);
  };

  const handleRemoveProduct = (productId: string) => {
    setSelectedProducts(selectedProducts.filter(p => p.value !== productId));
  };

  const handleSubmit = async (values: Record<string, any>) => {
    if (selectedProducts.length === 0) {
      message.error(t(`shared.flexible_products.form.error_no_products`));
      return;
    }
    try {
      setLoading(true);
      const data: Record<string, unknown> = {
        name: values.name,
        sku: values.sku,
        mechanism: values.mechanism,
        product_ids: selectedProducts.map(p => p.value),
      };
      if (role === 'operator' && !id && merchantId) {
        data.merchant_id = merchantId;
      }
      const response = id
        ? await flexibleProductService.update(id, data)
        : await flexibleProductService.create(data);
      if (response.success) {
        message.success(t(`shared.flexible_products.form.success`));
        goBack();
      } else {
        handleApiError(response, { t, fallbackMessage: t('shared.flexible_products.form.error') });
      }
    } catch (error) {
      console.error('Submit error:', error);
      handleNetworkError(error, { t });
    } finally {
      setLoading(false);
    }
  };

  const mechanismOptions = [
    { value: 'equally', label: t(`shared.flexible_products.mechanism.equally`) },
    { value: 'most_stock', label: t(`shared.flexible_products.mechanism.most_stock`) },
    { value: 'least_stock', label: t(`shared.flexible_products.mechanism.least_stock`) },
  ];

  // 已選商品：ProductCell 單欄堆疊（名稱在上，SKU 12px #999 在下）
  const productColumns = [
    {
      title: t(`shared.flexible_products.form.products`),
      key: 'product',
      render: (_: unknown, record: SelectedProductOption) => (
        <div>
          <div style={{ fontSize: 14, color: '#333' }}>{record.name}</div>
          <div style={{ fontSize: 12, color: '#999' }}>{record.sku}</div>
        </div>
      ),
    },
    {
      title: t('common.button.delete'),
      key: 'action',
      width: 80,
      align: 'right' as const,
      render: (_: unknown, record: SelectedProductOption) => (
        <Button
          type="link"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveProduct(record.value)}
        />
      ),
    },
  ];

  // ProductSelect 用的 merchantId 就是「此 form 操作的 merchant」 ——
  // wrapper 已在自己的 mode 內傳對的值（EDIT: record's merchant；NEW: 選的 merchant）。
  // null → admin session merchant 由 BE 從 token 推導。
  const productSelectMerchantId = merchantId;

  // Render-gate：sync 期間（dataLoading 抓 record；merchantDataLoading 切 context 到
  // record 的 merchant）整段 form 不能 mount，避免 ProductSelect / 下拉用錯 merchantId
  // 觸發 API 與 antd Select fallback 成 raw UUID。<Spin spinning> overlay 不夠 —
  // children 仍會 mount 並發出 stale fetch。
  if (dataLoading || merchantDataLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <Alert
          message={t(`shared.flexible_products.form.description`)}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Card title={t(`shared.flexible_products.form.basic_info`)}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{
              mechanism: 'equally'
            }}
          >
            <Form.Item
              name="name"
              label={t(`shared.flexible_products.form.name`)}
              rules={[{ required: true, message: t('common.validation.required') }]}
            >
              <Input placeholder={t(`shared.flexible_products.form.name_placeholder`)} />
            </Form.Item>
            <Form.Item
              name="sku"
              label={t(`shared.flexible_products.form.sku`)}
              rules={[{ required: true, message: t('common.validation.required') }]}
            >
              <Input placeholder={t(`shared.flexible_products.form.sku_placeholder`)} />
            </Form.Item>
            <Form.Item
              name="mechanism"
              label={
                <Space size={4}>
                  {t(`shared.flexible_products.form.mechanism`)}
                  <Tooltip
                    title={
                      <span style={{ whiteSpace: 'pre-line' }}>
                        {t(`shared.flexible_products.list.columns.mechanism_tooltip`)}
                      </span>
                    }
                  >
                    <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
                  </Tooltip>
                </Space>
              }
              rules={[{ required: true, message: t('common.validation.required') }]}
            >
              <Select
                options={mechanismOptions}
                placeholder={t(`shared.flexible_products.form.mechanism_placeholder`)}
              />
            </Form.Item>

            <Form.Item
              label={t(`shared.flexible_products.form.products`)}
              required
            >
              <ProductSelect
                productService={productService}
                merchantId={productSelectMerchantId}
                placeholder={t(`shared.flexible_products.form.products_placeholder`)}
                onSelect={handleSelectProduct as any}
                value={null}
                excludeIds={selectedProducts.map(p => p.value)}
                defaultOptions={selectedProducts}
                style={{ width: '100%' }}
              />
              {selectedProducts.length > 0 && (
                <Table
                  columns={productColumns}
                  dataSource={selectedProducts}
                  rowKey="value"
                  pagination={false}
                  size="small"
                  style={{ marginTop: 16 }}
                />
              )}
            </Form.Item>
          </Form>
        </Card>

        <FloatingActionBarSpacer />

        <FloatingActionBar
          role={role}
          onCancel={goBack}
          onSubmit={() => form.submit()}
          loading={loading}
        />
    </div>
  );
};

export default FlexibleProductEditView;
